import type { Coordinate } from "ol/coordinate";
import Feature from "ol/Feature";
import LineString from "ol/geom/LineString";
import Point from "ol/geom/Point";
import { type ProjectionLike, transform } from "ol/proj";
import { getAPIBaseUrl, getJwtToken } from "./environment";
import { getStopOverId, type StopOver } from "./HikingEditor";
import { pairwise } from "./utils";
import type { Discipline } from "./walking";

export const hikingTypes = ["hiking", "hiking_activity", "hiking_proposition"] as const;
export type HikingType = (typeof hikingTypes)[number];
export type Organisation =
  | "Aargauer Wanderwege"
  | "Appenzell Ausserrhoder Wanderwege"
  | "Appenzeller Wanderwege AI"
  | "Wanderwege beider Basel"
  | "Berner Wanderwege"
  | "Fribourg Rando"
  | "Fachorganisation"
  | "Genève Rando"
  | "Glarner Wanderwege"
  | "Wanderwege Graubünden"
  | "Jura Rando"
  | "Luzerner Wanderwege"
  | "Neuchâtel Rando"
  | "Nidwaldner Wanderwege"
  | "Obwaldner Wanderwege"
  | "St. Galler Wanderwege"
  | "Schaffhauser Wanderwege"
  | "Schwyzer Wanderwege"
  | "Solothurner Wanderwege"
  | "Thurgauer Wanderwege"
  | "TicinoSentieri"
  | "Urner Wanderwege"
  | "Valrando"
  | "Vaud Rando"
  | "Zuger Wanderwege"
  | "Zürcher Wanderwege"
  | "Liechtensteinische Wanderwege"
  | "Schweizer Wanderwege";

export type Category =
  | "alpine hike"
  | "mountain hike"
  | "snow shoe hike"
  | "hike"
  | "winter hike"
  | "schooltrip with hike"
  | "schooltrip without hike";

export type SACScale = "BLACK" | "BLUE" | "RED" | "T1" | "T2" | "T3" | "T4" | "T5" | "T6" | "WT1" | "WT2" | "WT3";
export type PhysicalDemand = "low" | "medium" | "high";

export type Triplet = [number, number, string];

export type UnsavedHiking = {
  control_points_with_stopovers: ServerControlPoint[];
  coordinates: Coordinate[][];
  discipline: Discipline;
  surfaces?: Triplet[][];
  structures?: Triplet[][];
  categories?: Triplet[][];
};

type ServerHikingWrite = UnsavedHiking & {
  uuid: string;
};

export type ServerHiking = ServerHikingWrite & {
  discipline: Discipline;
  organisation?: Organisation;
  physical_demand?: PhysicalDemand;
  custom_physical_demand?: boolean;
  height_difference_upwards?: number;
  custom_height_difference_upwards?: boolean;
  height_difference_downwards?: number;
  custom_height_difference_downwards?: boolean;
  altitude_min?: number;
  altitude_max?: number;
  altitude_median?: number;
};

type ServerControlPointWrite = {
  coordinates: Coordinate;
  name: string | null;
  features: unknown[] | null;
  distance: number | null;
  duration: number | null;
  custom_duration: boolean;
  custom_distance: boolean;
};

export type ServerControlPoint = ServerControlPointWrite & {
  uuid: string;
  vertex_index: number;
  custom_duration: boolean;
  custom_distance: boolean;
};

const SERVER_PROJECTION = "EPSG:4326";

/**
 * Returns the list of hiking of the current user.
 */
export async function getUserHiking(): Promise<ServerHiking> {
  const response = await fetch(`${getAPIBaseUrl()}/hiking`, {
    headers: {
      Authorization: `Bearer ${getJwtToken()}`,
    },
  });
  return await response.json();
}

export async function fetchHiking(uuid: string, hikingType: HikingType): Promise<ServerHiking> {
  const response = await fetch(`${getAPIBaseUrl()}/${hikingType}/${uuid}`, {
    headers: {
      Authorization: `Bearer ${getJwtToken()}`,
    },
  });
  return await response.json();
}

export function readFeatures(
  hiking: ServerHiking | UnsavedHiking,
  projection: ProjectionLike,
): Feature<LineString | Point>[] {
  const features: Feature<LineString | Point>[] = [];

  let vertex_count = 0;
  hiking.coordinates.forEach((coordinates, part) => {
    const control_points = hiking.control_points_with_stopovers.filter(
      (point) => point.vertex_index >= vertex_count && point.vertex_index < vertex_count + coordinates.length,
    );
    // change the vertex_index to be relative to the part
    control_points.forEach((point) => {
      point.vertex_index -= vertex_count;
    });

    control_points.forEach((point, index) => {
      const feature = new Feature({
        geometry: new Point(point.coordinates).transform(SERVER_PROJECTION, projection),
        type: "controlPoint",
        part: part,
        snapped: true,
        index: index,
        stopover: point.name !== null,
        sww_name: point.name,
        sww_features: point.features,
        sww_duration: point.custom_duration ? point.duration : null,
        sww_custom_duration: point.custom_duration,
        sww_distance: point.custom_distance ? point.distance : null,
        sww_custom_distance: point.custom_distance,
      });
      feature.set("sww_id", point.uuid);
      if (index === 0) {
        feature.set("subtype", "first");
      } else if (index === control_points.length - 1) {
        feature.set("subtype", "last");
      }
      features.push(feature);
    });

    for (const [previous, current] of pairwise(control_points)) {
      // 'current.vertex_index +1' to include the current vertex in the segment
      const segment_coordinates = coordinates.slice(previous.vertex_index, current.vertex_index + 1);

      const partSurfaces = sliceTriplet(hiking.surfaces?.[part] ?? [], previous.vertex_index, current.vertex_index);
      const partStructures = sliceTriplet(hiking.structures?.[part] ?? [], previous.vertex_index, current.vertex_index);
      const partCategories = sliceTriplet(hiking.categories?.[part] ?? [], previous.vertex_index, current.vertex_index);

      const segment = new Feature({
        geometry: new LineString(segment_coordinates).transform(SERVER_PROJECTION, projection),
        type: "segment",
        part: part,
        snapped: true,
        surfaces: partSurfaces,
        structures: partStructures,
        hiking_categories: partCategories,
      });
      features.push(segment);
    }

    vertex_count += coordinates.length;
  });
  return features;
}

function sliceTriplet(triplets: Triplet[], start: number, end: number): GraphhopperTriplet[] {
  return triplets
    .map(fromTriplet)
    .filter((t) => t.start >= start && t.end <= end)
    .map((t) => ({
      start: t.start - start,
      end: t.end - start,
      type: t.type,
    }));
}

function removeEmptyArrays<T>(arrays: T[][]): T[][] | undefined {
  if (arrays.every((arr) => arr.length === 0)) {
    return undefined;
  }
  return arrays;
}

export function writeFeatures(
  all_segments: readonly Feature<LineString>[][],
  all_control_points: readonly Feature<Point>[][],
  stopovers: Record<string, StopOver>,
  projection: ProjectionLike,
): any {
  const result = {
    control_points_with_stopovers: [] as unknown[],
    coordinates: [] as Coordinate[][],
    surfaces: [] as Triplet[][],
    structures: [] as Triplet[][],
    categories: [] as Triplet[][],
  };

  if (all_segments.length !== all_control_points.length) {
    throw new Error("Not the same number of segments and control points");
  }
  for (let i = 0; i < all_segments.length; i++) {
    const segments = all_segments[i];
    const control_points = all_control_points[i];

    const segments_coordinates = segments.map((segment) => {
      return segment
        .get("profile")
        .map((c: Coordinate) => c.slice(0, 3))
        .map((c: Coordinate) => transform(c, projection, SERVER_PROJECTION));
    });

    // Merge all data from segments within this part
    // Each segment has its own surfaces/structures/categories with indices relative to that segment
    // We need to merge them into a single array with adjusted indices for the entire part
    const data = mergeSegmentDetails(segments);
    const mergedSurfaces = data.surfaces;
    const mergedStructures = data.structures;
    const mergedCategories = data.categories;

    // weld coordinates (remove duplicates at junctions)
    const segments_points_coordinates = [];
    if (segments_coordinates.length > 0) {
      segments_points_coordinates.push(...segments_coordinates[0]);
      for (let j = 1; j < segments_coordinates.length; j++) {
        segments_points_coordinates.push(...segments_coordinates[j].slice(1));
      }
    }

    // Calculate vertex indices for control points
    let currentVertexIndex = 0;
    const controlPointIndices = [0]; // Index for first control point is 0
    for (const segment of segments) {
      const segmentLen = segment.get("profile").length; // Use profile length as it matches coordinates
      // With welding, the next control point is at current + length - 1
      currentVertexIndex += segmentLen - 1;
      controlPointIndices.push(currentVertexIndex);
    }

    // instead of using the point geometry, we use the segment geometry to always have XYZ coordinates
    const points_coordinates = segments_coordinates.map((coordinates) => coordinates.at(0));
    points_coordinates.push(segments_coordinates.at(-1)?.at(-1));

    const points = control_points.map((point, index) => {
      const stopover = stopovers[getStopOverId(point)];
      const vertex_index = controlPointIndices[index];

      const common = {
        vertex_index,
        coordinates: points_coordinates[index],
      };

      if (stopover) {
        if (stopover.properties.customName === null && stopover.properties.swissNames.length === 0) {
          throw new Error("A stopover must have a name");
        }
        return {
          ...common,
          uuid: getStopOverId(point),
          name: stopover.properties.customName ?? stopover.properties.swissNames[0],
          features: stopover.properties.features,
          duration: stopover.properties.customDuration ?? null,
          custom_duration: stopover.properties.customDuration !== undefined,
          distance: stopover.properties.customDistance ?? null,
          custom_distance: stopover.properties.customDistance !== undefined,
        };
      }
      return common;
    });

    result.control_points_with_stopovers.push(...points);
    result.coordinates.push(segments_points_coordinates);
    result.surfaces.push(mergedSurfaces.map(toTriplet));
    result.structures.push(mergedStructures.map(toTriplet));
    result.categories.push(mergedCategories.map(toTriplet));
  }
  // Remove empty arrays
  // @ts-expect-error: value can be undefined
  result.surfaces = removeEmptyArrays(result.surfaces);
  // @ts-expect-error: value can be undefined
  result.structures = removeEmptyArrays(result.structures);
  // @ts-expect-error: value can be undefined
  result.categories = removeEmptyArrays(result.categories);

  return result;
}

export async function createHiking(
  hikingType: HikingType,
  geometries: ServerHikingWrite,
  properties: Record<string, unknown>,
): Promise<ServerHiking> {
  const response = await fetch(`${getAPIBaseUrl()}/${hikingType}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getJwtToken()}`,
    },
    body: JSON.stringify({ ...geometries, ...properties }),
  });
  return await response.json();
}

export async function updateHiking(
  uuid: string,
  hikingType: HikingType,
  geometries: ServerHikingWrite,
  properties: Record<string, unknown>,
): Promise<ServerHiking> {
  const response = await fetch(`${getAPIBaseUrl()}/${hikingType}/${uuid}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getJwtToken()}`,
    },
    body: JSON.stringify({ ...geometries, ...properties }),
  });
  return await response.json();
}

export type GraphhopperTriplet = { start: number; end: number; type: string };

export function toTriplet(data: GraphhopperTriplet): Triplet {
  return [data.start, data.end, data.type];
}

export function fromTriplet(data: Triplet): GraphhopperTriplet {
  return { start: data[0], end: data[1], type: data[2] };
}

export function mergeSegmentDetails(segments: readonly Feature<LineString>[]): {
  surfaces: GraphhopperTriplet[];
  structures: GraphhopperTriplet[];
  categories: GraphhopperTriplet[];
} {
  const mergedSurfaces: GraphhopperTriplet[] = [];
  const mergedStructures: GraphhopperTriplet[] = [];
  const mergedCategories: GraphhopperTriplet[] = [];
  let offset = 0;

  for (const segment of segments) {
    const segmentProfile = segment.get("profile");

    // Adjust indices and merge surfaces
    for (const detail of segment.get("surfaces") ?? []) {
      mergedSurfaces.push({
        start: detail.start + offset,
        end: detail.end + offset,
        type: detail.type,
      });
    }

    // Adjust indices and merge structures
    for (const detail of segment.get("structures") ?? []) {
      mergedStructures.push({
        start: detail.start + offset,
        end: detail.end + offset,
        type: detail.type,
      });
    }

    // Adjust indices and merge hiking categories
    for (const detail of segment.get("hiking_categories") ?? []) {
      mergedCategories.push({
        start: detail.start + offset,
        end: detail.end + offset,
        type: detail.type,
      });
    }

    // Increment offset by the number of edges (points - 1)
    // This effectively welds the segments by overlapping the shared vertex
    offset += segmentProfile.length - 1;
  }
  return {
    surfaces: mergedSurfaces,
    structures: mergedStructures,
    categories: mergedCategories,
  };
}
